import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { OnyxEvent } from '../models/event';
import { EventHttpService } from './event.http.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { BulkUploadResponse } from '../models/bulk-upload-response';
import { StaySummary } from '../models/stay-summary';
import { RatePlanCodeValidationRequest } from '../models/rate-plan-code-validation-request';
import { ValidationError } from '../models/validation-error';
import { RatePlanCodeReservationCounts } from '../models/rate-plan-code-reservation-counts';

@Injectable()
export class EventStore {

  private mySelectedEvent: BehaviorSubject<OnyxEvent> = new BehaviorSubject(null);

  constructor(private eventHttpService: EventHttpService,
              private flashMessagesService: FlashMessagesService) {
  }

  /**
   * Returns an observable containing the current event.
   * Used in any case when data needs to be shown on a screen.
   *
   * @returns {Observable<T>}
   */
  get selectedEvent(): Observable<OnyxEvent> {
    return this.mySelectedEvent.asObservable();
  }

  resetSelectedEvent() {
    this.mySelectedEvent.next(null);
  }

  /**
   * Retrieves event.  This method returns an observable
   * and should be used only in route resolvers.
   *
   * @param eventId
   * @returns {Observable<OnyxEvent>}
   */
  getEvent(eventId: number): Observable<OnyxEvent> {
    return this.eventHttpService.get(eventId)
      .catch((error: any) => {
        // Catch error here so resolver also displays error.
        this.flashMessagesService.error('MESSAGES.ERROR.GET_EVENT', `Error retrieving OnyxEvent: ${error}`);
        return Observable.throw(error);
      })
      .map((res: OnyxEvent) => {
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  addEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.add(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  addAgencyEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.addAsAgency(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  updateEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.update(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  updateAgencyEvent(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.updateAsAgency(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  updateEventIndicators(newRec: OnyxEvent): Observable<OnyxEvent> {
    return this.eventHttpService.updateIndicators(newRec)
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.SAVE_EVENT');
        this.mySelectedEvent.next(res);
        return res;
      });
  }

  downloadInvalidEventsCsv(fileId: number): Observable<any> {
    return this.eventHttpService.downloadInvalidEventsCsv(fileId);
  }

  deleteEvent(eventId: number): Observable<OnyxEvent> {
    return this.eventHttpService.delete(eventId)
      .catch((error => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_EVENT');
        return Observable.throw(error);
      }))
      .map((res: OnyxEvent) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.DELETE_EVENT');
        this.mySelectedEvent.next(null);
        return res;
      });
  }

  importEventsViaCsv(file: File): Observable<BulkUploadResponse> {
    return this.eventHttpService.importEventsViaCsv(file)
      .catch((error => {
        return Observable.throw(error);
      }))
      .map((importResponse: BulkUploadResponse) => {
        return importResponse;
      });
  }

  getStaySummary(event: OnyxEvent): Observable<StaySummary> {
    return this.eventHttpService.getStaySummary(event)
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.GET_EVENT', `Error retrieving stay summary: ${error}`);
        return Observable.throw(error);
      });
  }

  getRatePlanCodeValidation(request: RatePlanCodeValidationRequest): Observable<ValidationError[]> {
    return this.eventHttpService.getRatePlanCodeValidation(request)
      .catch(error => {
        return Observable.throw(error);
      }
    );
  }

  getReservationCountForRatePlanCode(eventId: number, ratePlanCode: string): Observable<RatePlanCodeReservationCounts> {
    return this.eventHttpService.getReservationCountForRatePlanCode(eventId, ratePlanCode)
      .catch(error => {
        return Observable.throw(error);
      });
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/event.store.ts